import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { SaveStatus } from '@personal-dashboard/ui';
import {
  LocalField,
  LocalSection,
  CREATE_CUSTOM_FIELD_SECTION,
  UPDATE_CUSTOM_FIELD_SECTION,
  DELETE_CUSTOM_FIELD_SECTION,
  TOGGLE_CUSTOM_FIELD_PUBLISHED,
} from '../components/customFieldsQueries';

export function useCustomFieldSections(data: any) {
  const [sections, setSections] = useState<LocalSection[]>([]);
  const [deleteSectionId, setDeleteSectionId] = useState<string | null>(null);
  const [saveStatuses, setSaveStatuses] = useState<Record<string, SaveStatus>>({});

  const [createSection] = useMutation(CREATE_CUSTOM_FIELD_SECTION);
  const [updateSection] = useMutation(UPDATE_CUSTOM_FIELD_SECTION);
  const [deleteSection] = useMutation(DELETE_CUSTOM_FIELD_SECTION);
  const [toggleFieldPublished] = useMutation(TOGGLE_CUSTOM_FIELD_PUBLISHED, {
    refetchQueries: ['GetCustomFieldSections'],
  });

  useEffect(() => {
    if (data?.getCustomFieldSections) {
      setSections(
        data.getCustomFieldSections.map((sec: any) => ({
          id: sec.id,
          title: sec.title,
          fields: sec.fields.map((f: any) => ({
            id: f.id,
            name: f.name,
            type: f.type,
            options: f.options || [],
            published: f.published !== false,
          })),
        }))
      );
    }
  }, [data]);

  const handleCreateSection = async (title: string) => {
    const placeholderId = `temp_${Date.now()}`;
    const optimisticSection: LocalSection = {
      id: placeholderId,
      title,
      fields: [],
    };
    setSections((prev) => [...prev, optimisticSection]);
    try {
      const { data: newData } = await createSection({
        variables: { title },
      });
      if (newData?.createCustomFieldSection) {
        setSections((prev) =>
          prev.map((s) =>
            s.id === placeholderId
              ? {
                  id: newData.createCustomFieldSection.id,
                  title: newData.createCustomFieldSection.title,
                  fields: (newData.createCustomFieldSection.fields || []).map((f: any) => ({
                    id: f.id,
                    name: f.name,
                    type: f.type,
                    options: f.options || [],
                    published: f.published !== false,
                  })),
                }
              : s
          )
        );
      }
    } catch (err) {
      console.error('Create section failed:', err);
      setSections((prev) => prev.filter((s) => s.id !== placeholderId));
    }
  };

  const handleDeleteSection = async (id: string) => {
    setDeleteSectionId(id);
  };

  const handleConfirmDeleteSection = async () => {
    if (!deleteSectionId) return;
    const id = deleteSectionId;
    const prev = sections;
    setSections((prev) => prev.filter((s) => s.id !== id));
    setDeleteSectionId(null);
    try {
      await deleteSection({ variables: { id } });
    } catch (err) {
      console.error('Delete section failed:', err);
      setSections(prev);
    }
  };

  const handleSaveSection = async (section: LocalSection) => {
    const cleanFields = section.fields.map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      options: f.options,
      published: f.published !== false,
    }));
    setSaveStatuses((prev) => ({ ...prev, [section.id]: 'saving' as SaveStatus }));
    try {
      const { data: result } = await updateSection({
        variables: {
          id: section.id,
          title: section.title,
          fields: cleanFields,
        },
      });
      if (result?.updateCustomFieldSection) {
        setSections((prev) =>
          prev.map((s) =>
            s.id === section.id
              ? {
                  id: result.updateCustomFieldSection.id,
                  title: result.updateCustomFieldSection.title,
                  fields: (result.updateCustomFieldSection.fields || []).map((f: any) => ({
                    id: f.id,
                    name: f.name,
                    type: f.type,
                    options: f.options || [],
                    published: f.published !== false,
                  })),
                }
              : s
          )
        );
      }
      setSaveStatuses((prev) => ({ ...prev, [section.id]: 'saved' as SaveStatus }));
      setTimeout(() => {
        setSaveStatuses((prev) => ({ ...prev, [section.id]: 'idle' as SaveStatus }));
      }, 2000);
    } catch (err) {
      console.error('Save section failed:', err);
      setSaveStatuses((prev) => ({ ...prev, [section.id]: 'error' as SaveStatus }));
    }
  };

  const handleTogglePublish = (sectionId: string, fieldId: string, published: boolean) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.map((f) => (f.id === fieldId ? { ...f, published } : f)),
        };
      })
    );
    toggleFieldPublished({
      variables: { fieldId, published },
    }).catch((err) => {
      console.error('Toggle publish failed:', err);
    });
  };

  const handleUpdateSectionTitle = (sectionId: string, title: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, title } : s))
    );
  };

  const handleAddField = (sectionId: string) => {
    const newField: LocalField = {
      id: `field_${Date.now()}`,
      name: 'New Custom Field',
      type: 'TEXT',
      options: [],
      published: true,
    };
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, fields: [...s.fields, newField] } : s))
    );
  };

  const handleDeleteField = (sectionId: string, fieldId: string) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.filter((f) => f.id !== fieldId),
        };
      })
    );
  };

  const handleUpdateField = (sectionId: string, fieldId: string, updates: Partial<LocalField>) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
        };
      })
    );
  };

  const handleAddOption = (sectionId: string, fieldId: string, optionValue: string) => {
    if (!optionValue.trim()) return;
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.map((f) => {
            if (f.id !== fieldId) return f;
            if (f.options.includes(optionValue.trim())) return f;
            return { ...f, options: [...f.options, optionValue.trim()] };
          }),
        };
      })
    );
  };

  const handleRemoveOption = (sectionId: string, fieldId: string, optionToRemove: string) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== sectionId) return s;
        return {
          ...s,
          fields: s.fields.map((f) => {
            if (f.id !== fieldId) return f;
            return { ...f, options: f.options.filter((opt) => opt !== optionToRemove) };
          }),
        };
      })
    );
  };

  return {
    sections,
    saveStatuses,
    deleteSectionId,
    setDeleteSectionId,
    handleCreateSection,
    handleDeleteSection,
    handleConfirmDeleteSection,
    handleSaveSection,
    handleTogglePublish,
    handleUpdateSectionTitle,
    handleAddField,
    handleDeleteField,
    handleUpdateField,
    handleAddOption,
    handleRemoveOption,
  };
}
